import { hasCompleteBillingDetails } from '@/components/billing/BillingDetailsForm'
import { useJobillaOrgId } from '@/hooks'
import { useAuth, useEntitlements, usePrivateChannel } from '@/lib'
import { Button, logger, SettingsNav, SubtleLoader } from '@/ui'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import {
	type BillingProfileData,
	broadcastChannel,
	paymentMethodEvent,
	paymentMethodIndexQueryKey,
	type PlanDataTier,
	type PlanInterval,
	planInterval,
	profileEvent,
	profilesIndexQueryKey,
	subscriptionAccessDataReason,
	useOrganisationRelationShow,
	usePaymentMethodIndex,
	usePlanIndex,
	useProfilesIndex,
} from '@jobilla/nexus-sdk'
import { Link } from '@tanstack/react-router'
import { upperFirst } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ActivationStep } from './activation-step'
import { CardStep, profileCards } from './card-step'
import { ConfirmStep } from './confirm-step'
import { DetailsStep } from './details-step'
import { AskAnAdmin, PaywallNav, PaywallReason } from './paywall'
import { PlanStep } from './plan-step'

type Step = 'plan' | 'details' | 'card' | 'confirm' | 'activation'

export function SubscribePage({ companyId }: { companyId: string }) {
	return (
		<div className="bg-grey-50 min-h-screen">
			<PaywallNav />
			<div className="mx-auto max-w-[1100px] px-8 py-10">
				<SubscribeFlow companyId={companyId} />
			</div>
		</div>
	)
}

export function SubscribeSettingsPage({ companyId }: { companyId: string }) {
	return (
		<SettingsNav active="billing">
			<SubscribeFlow companyId={companyId} />
		</SettingsNav>
	)
}

function SubscribeFlow({ companyId }: { companyId: string }) {
	const { t } = useTranslation('common')
	const { can } = useAuth()
	const organisationId = useJobillaOrgId()
	const { subscriptionAccess } = useEntitlements()

	const [steps, setSteps] = useState<Step[]>(['plan'])
	const [interval, setInterval] = useState<PlanInterval>(planInterval.yearly)
	const [tier, setTier] = useState<PlanDataTier | null>(null)
	const [profileId, setProfileId] = useState<string | null>(null)
	const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null)

	const step = steps[steps.length - 1]
	const canManage = can('billing:manage')

	const blockedReason = subscriptionAccess && !subscriptionAccess.hasAccess ? subscriptionAccess.reason : null
	const trackedPaywallView = useRef(false)

	useEffect(() => {
		if (!blockedReason || trackedPaywallView.current) {
			return
		}
		trackedPaywallView.current = true
		logger.track('Subscription: paywall viewed', {
			category: 'billing',
			data: { reason: blockedReason, canManage },
		})
	}, [blockedReason, canManage])

	const parentOrganisation = useOrganisationRelationShow(organisationId, { enabled: !!organisationId }).data?.parent
		?? null

	const profilesQuery = useProfilesIndex(organisationId, { enabled: !!organisationId && canManage })
	const paymentMethodsQuery = usePaymentMethodIndex(organisationId, undefined, {
		enabled: !!organisationId && canManage,
	})

	// Profiles can change in another tab, and cards land via webhook.
	usePrivateChannel(
		organisationId && canManage ? broadcastChannel.OrganisationProfiles({ organisation: organisationId }) : null,
		{
			[profileEvent.Updated]: [profilesIndexQueryKey],
			[profileEvent.PrimaryChanged]: [profilesIndexQueryKey],
			[profileEvent.Archived]: [profilesIndexQueryKey],
		},
	)
	usePrivateChannel(
		organisationId && canManage
			? broadcastChannel.OrganisationPaymentMethod({ organisation: organisationId })
			: null,
		{
			[paymentMethodEvent.Updated]: [paymentMethodIndexQueryKey],
			[paymentMethodEvent.PrimaryChanged]: [paymentMethodIndexQueryKey],
			[paymentMethodEvent.Deleted]: [paymentMethodIndexQueryKey],
		},
	)

	const profiles = useMemo(
		() => (profilesQuery.data?.data ?? []).filter((candidate) => !candidate.archived),
		[profilesQuery.data],
	)
	const profile = profiles.find((candidate) => candidate.id === profileId)
		?? profiles.find((candidate) => candidate.isPrimary)
		?? profiles[0]
		?? null

	const plansQuery = usePlanIndex(organisationId, profile ? { profile: profile.id } : undefined, {
		enabled: !!organisationId && canManage,
	})

	const cards = useMemo(
		() => profile ? profileCards(paymentMethodsQuery.data?.data ?? [], profile) : [],
		[paymentMethodsQuery.data, profile],
	)
	const card = cards.find((candidate) => candidate.id === paymentMethodId) ?? null
	const plan = plansQuery.data?.find((candidate) => candidate.tier === tier) ?? null

	useEffect(() => {
		if (card || cards.length === 0) {
			return
		}
		setPaymentMethodId((cards.find((candidate) => candidate.isDefault) ?? cards[0]).id)
	}, [card, cards])

	const goTo = useCallback((next: Step) => setSteps((previous) => [...previous, next]), [])
	const goBack = () => setSteps((previous) => previous.length > 1 ? previous.slice(0, -1) : previous)

	function stepAfterDetails(savedProfile: BillingProfileData): Step {
		return profileCards(paymentMethodsQuery.data?.data ?? [], savedProfile).length > 0 ? 'confirm' : 'card'
	}

	function choosePlan(chosenTier: PlanDataTier) {
		logger.track('Subscription: plan chosen', { category: 'billing', data: { tier: chosenTier, interval } })
		setTier(chosenTier)
		goTo(profile && hasCompleteBillingDetails(profile) ? stepAfterDetails(profile) : 'details')
	}

	function changeProfile(nextProfileId: string) {
		setProfileId(nextProfileId)
		setPaymentMethodId(null)
	}

	const selectCard = useCallback((nextPaymentMethodId: string) => setPaymentMethodId(nextPaymentMethodId), [])

	if (parentOrganisation) {
		return (
			<div className="mx-auto max-w-lg space-y-3 text-center">
				<p className="text-sm text-grey-600">
					{t('subscribe.childOrganisation', { parent: parentOrganisation.name })}
				</p>
				<Link
					to="/company/$companyId"
					params={{ companyId }}
					className="text-sm font-medium text-brand-700 hover:underline"
				>
					{t('subscribe.active.continue')}
				</Link>
			</div>
		)
	}

	if (step !== 'activation' && subscriptionAccess?.reason === subscriptionAccessDataReason.subscribed) {
		return (
			<div className="mx-auto max-w-lg space-y-3 text-center">
				<h1 className="text-xl font-bold text-grey-900">{t('subscribe.alreadySubscribed')}</h1>
				<Link
					to="/company/$companyId"
					params={{ companyId }}
					className="text-sm font-medium text-brand-700 hover:underline"
				>
					{t('subscribe.active.continue')}
				</Link>
			</div>
		)
	}

	if (!canManage) {
		return (
			<div>
				{subscriptionAccess && !subscriptionAccess.hasAccess
					? <PaywallReason reason={subscriptionAccess.reason} />
					: null}
				<AskAnAdmin />
			</div>
		)
	}

	const needsPrerequisites = step !== 'plan' && step !== 'activation'
	const failedPrerequisites = needsPrerequisites
		? [profilesQuery, paymentMethodsQuery].filter((query) => query.isError && !query.data)
		: []
	const isLoadingPrerequisites = needsPrerequisites
		&& (failedPrerequisites.length > 0 || profilesQuery.isLoading || paymentMethodsQuery.isLoading)

	return (
		<div>
			{steps.length > 1 && step !== 'activation'
				? (
					<button
						type="button"
						onClick={goBack}
						className="mb-6 flex items-center gap-1.5 text-sm font-medium text-grey-600 hover:text-grey-900 cursor-pointer"
					>
						<ArrowLeftIcon className="h-4 w-4" />
						{t('subscribe.back')}
					</button>
				)
				: null}

			{failedPrerequisites.length > 0
				? (
					<div className="mx-auto max-w-lg rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
						<p className="mb-3">{t('subscribe.prerequisitesLoadError')}</p>
						<Button
							type="neutral"
							onClick={() => failedPrerequisites.forEach((query) => void query.refetch())}
						>
							{t('common.tryAgain')}
						</Button>
					</div>
				)
				: isLoadingPrerequisites
				? <SubtleLoader delay={0} />
				: null}

			{step === 'plan' && subscriptionAccess && !subscriptionAccess.hasAccess
				? <PaywallReason reason={subscriptionAccess.reason} />
				: null}

			{step === 'plan'
				? (
					<PlanStep
						profileId={profile?.id ?? null}
						interval={interval}
						onIntervalChange={setInterval}
						onChoose={choosePlan}
					/>
				)
				: null}

			{step === 'details' && !isLoadingPrerequisites
				? (
					<DetailsStep
						profiles={profiles}
						profile={profile}
						onProfileChange={changeProfile}
						onSaved={(savedProfile) => {
							logger.track('Subscription: details saved', { category: 'billing' })
							setProfileId(savedProfile.id)
							goTo(stepAfterDetails(savedProfile))
						}}
					/>
				)
				: null}

			{step === 'card' && !isLoadingPrerequisites && profile
				? (
					<CardStep
						organisationId={organisationId}
						profile={profile}
						selectedId={paymentMethodId}
						onSelect={selectCard}
						onContinue={() => goTo('confirm')}
					/>
				)
				: null}

			{step === 'confirm' && !isLoadingPrerequisites && plan && profile && card
				? (
					<ConfirmStep
						plan={plan}
						interval={interval}
						profile={profile}
						card={card}
						onEditPlan={() => setSteps(['plan'])}
						onEditDetails={() => goTo('details')}
						onEditCard={() => goTo('card')}
						onSubscribed={() => goTo('activation')}
					/>
				)
				: null}

			{step === 'activation' && tier
				? <ActivationStep companyId={companyId} planName={upperFirst(tier)} />
				: null}
		</div>
	)
}
